'use client';

import { c as _c } from "react/compiler-runtime";
import { Card, type CardProps } from '@morpho-org/ui-v6/server';
import { cn } from '@morpho-org/ui-v6/utils';
import { motion } from 'motion/react';
import React, { type MouseEventHandler, type ReactNode } from 'react';
import { getDashboardConfig } from './CardDashboard.config';
interface CardDashboardRootProps extends CardProps {
  isExpanded?: boolean;
  isHovered?: boolean;
}

/**
 * Dashboard card shell. Hover styling targets the dashboard hover-prevent and
 * expand-button class hooks from `DashboardClass`
 * (`prevent-dashboard-card-hover` / `dashboard-card-button-expand`) — keep these
 * literals in sync with `@/contexts/Dashboard/Dashboard.context`.
 */
const CardDashboardRoot = t0 => {
  const $ = _c(15);
  let className;
  let isExpanded;
  let isHovered;
  let props;
  if ($[0] !== t0) {
    ({
      isExpanded,
      isHovered,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = isExpanded;
    $[3] = isHovered;
    $[4] = props;
  } else {
    className = $[1];
    isExpanded = $[2];
    isHovered = $[3];
    props = $[4];
  }
  let t1;
  if ($[5] !== className || $[6] !== isExpanded || $[7] !== isHovered) {
    let t2;
    if ($[9] !== isExpanded || $[10] !== isHovered) {
      t2 = isHovered && !isExpanded && ["cursor-pointer bg-accent [transition:background-color_0.05s_ease-in]", "[&_.dashboard-card-button-expand]:bg-accent", "[&_.dashboard-card-button-expand]:[transition:background-color_0.05s_ease-in]", "[&_.dashboard-card-button-expand_svg]:text-foreground"];
      $[9] = isExpanded;
      $[10] = isHovered;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    t1 = cn("p-lg gap-lg h-full w-full [transition:background-color_0.2s_ease-out]", "[&_.dashboard-card-button-expand]:[transition:background-color_0.5s_ease-out]", "max-sm:px-md max-sm:py-lg", "lg:[&:hover:not(:has(.prevent-dashboard-card-hover:hover))]:cursor-pointer", !isExpanded && ["lg:[&:hover:not(:has(.prevent-dashboard-card-hover:hover))]:bg-accent", "lg:[&:hover:not(:has(.prevent-dashboard-card-hover:hover))]:[transition:background-color_0.05s_ease-in]", "lg:[&:hover:not(:has(.prevent-dashboard-card-hover:hover))_.dashboard-card-button-expand]:bg-accent", "lg:[&:hover:not(:has(.prevent-dashboard-card-hover:hover))_.dashboard-card-button-expand]:[transition:background-color_0.05s_ease-in]", "lg:[&:hover:not(:has(.prevent-dashboard-card-hover:hover))_.dashboard-card-button-expand_svg]:text-foreground"], t2, isExpanded ? "[&:has(.prevent-dashboard-card-hover:hover)]:cursor-default" : "[&:has(.prevent-dashboard-card-hover:hover)]:cursor-pointer", className);
    $[5] = className;
    $[6] = isExpanded;
    $[7] = isHovered;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[12] !== props || $[13] !== t1) {
    t2 = <Card className={t1} {...props} />;
    $[12] = props;
    $[13] = t1;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
};
export interface CardDashboardLayoutProps {
  chartMyDepositsSlot: ReactNode;
  earnApyCardSlot: ReactNode;
  isExpanded?: boolean;
  isSmall?: boolean;
  isHovered?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onClick?: MouseEventHandler<HTMLDivElement>;
}
const CardDashboardLayout = t0 => {
  const $ = _c(26);
  const {
    chartMyDepositsSlot,
    earnApyCardSlot,
    isExpanded,
    isSmall,
    isHovered,
    onMouseEnter,
    onMouseLeave,
    onClick
  } = t0;
  let t1;
  if ($[0] !== isExpanded || $[1] !== isSmall) {
    t1 = getDashboardConfig({
      isExpanded,
      isSmall
    });
    $[0] = isExpanded;
    $[1] = isSmall;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const dashboardConfig = t1;
  if (isSmall) {
    let t2;
    if ($[3] !== chartMyDepositsSlot || $[4] !== isExpanded) {
      t2 = <CardDashboardRoot withBorder={true} isExpanded={isExpanded}>{chartMyDepositsSlot}</CardDashboardRoot>;
      $[3] = chartMyDepositsSlot;
      $[4] = isExpanded;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    if ($[6] !== earnApyCardSlot) {
      t3 = <div className="flex w-full">{earnApyCardSlot}</div>;
      $[6] = earnApyCardSlot;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] !== t2 || $[9] !== t3) {
      t4 = <div data-testid="dashboard-hero" className="gap-sm flex w-full flex-col">{t2}{t3}</div>;
      $[8] = t2;
      $[9] = t3;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  let t2;
  if ($[11] !== dashboardConfig.height) {
    t2 = {
      height: dashboardConfig.height
    };
    $[11] = dashboardConfig.height;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      duration: 0.3
    };
    t4 = {
      width: "100%"
    };
    $[13] = t3;
    $[14] = t4;
  } else {
    t3 = $[13];
    t4 = $[14];
  }
  let t5;
  if ($[15] !== chartMyDepositsSlot || $[16] !== earnApyCardSlot || $[17] !== isExpanded || $[18] !== isHovered || $[19] !== onClick || $[20] !== onMouseEnter || $[21] !== onMouseLeave) {
    t5 = <CardDashboardRoot withBorder={true} isExpanded={isExpanded} isHovered={isHovered} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onClick={onClick}>{chartMyDepositsSlot}{earnApyCardSlot}</CardDashboardRoot>;
    $[15] = chartMyDepositsSlot;
    $[16] = earnApyCardSlot;
    $[17] = isExpanded;
    $[18] = isHovered;
    $[19] = onClick;
    $[20] = onMouseEnter;
    $[21] = onMouseLeave;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== t2 || $[24] !== t5) {
    t6 = <motion.div key="card-dashboard-layout" data-testid="dashboard-hero" layout={true} animate={t2} initial={false} transition={t3} style={t4}>{t5}</motion.div>;
    $[23] = t2;
    $[24] = t5;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  return t6;
};
export default CardDashboardLayout;